import StreetViewPhoto from './StreetViewPhoto';
import { tx } from '../../i18n';
import { splitAddress } from '../../lib/report';
import type { AnalysisSummaryResponse } from '../../types/api';

interface ReportHeadProps {
  summary: AnalysisSummaryResponse;
  paid: boolean;
}

/**
 * The photo and the address, the way a listing opens.
 *
 * <p>Deliberately plain: the picture, then what this page is about. The verdict and the score live
 * in the summary beside the report, so the top of the page does not have to carry them too.
 */
export default function ReportHead({ summary, paid }: ReportHeadProps) {
  const { title, rest } = splitAddress(summary.addressLine);

  /*
   * The postcode is added only when the address line does not already end in it.
   *
   * Most address lines from the register carry the postcode, so appending it unconditionally
   * printed it twice - "London, W9 2HQ, W9 2HQ, Westminster" - which reads as a bug in the data
   * rather than in the joining, and undermines the one thing this report is selling.
   */
  const says = (part: string | null | undefined, value: string) =>
    (part ?? '').replace(/\s+/g, ' ').toUpperCase().includes(value.replace(/\s+/g, ' ').toUpperCase());
  const place = [
    rest,
    says(rest, summary.postcode) ? null : summary.postcode,
    says(rest, summary.borough) ? null : summary.borough,
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <header className="report-head">
      <StreetViewPhoto analysisId={summary.analysisId} />
      <p className="report-eyebrow">
        {paid ? tx('Full property report', 'Tam konut raporu',
    'Pełny raport o mieszkaniu',
    'Relatório completo do imóvel',
    'Informe completo de la vivienda') : tx('Free property report', 'Ücretsiz konut raporu',
    'Bezpłatny raport o mieszkaniu',
    'Relatório gratuito do imóvel',
    'Informe gratuito de la vivienda')}
      </p>
      <h1 className="report-head__title">{title}</h1>
      <p className="report-head__place">{place}</p>
    </header>
  );
}
